import React, { useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  getCombinedSnapshot,
  drainAll,
  onAnyChange,
  noteSnapshot,
  checkStuckPending,
} from "@/lib/syncTruth";

/**
 * SyncSuccessListener — Founder "One Sync Truth" + offline success
 * feedback + stuck-pending safety net driver.
 *
 * Mounted ONCE at the App root (outside <Shell>, so it's active on
 * every page including SheetViewer which bypasses Shell entirely).
 * Renders nothing. Responsibilities:
 *
 *   1. Auto-drain all 3 offline queues the moment the browser comes
 *      back online with pending work — the single canonical trigger so
 *      no individual pill has to (re)implement it.
 *   2. Fire a quiet, non-blocking "Offline work synced" toast — but
 *      ONLY after a real drain of genuinely-pending work completes,
 *      never merely because connectivity returned. This is the
 *      distinction between CONNECTED and ACTUALLY SYNCED.
 *   3. Report stuck-pending metadata (queue engine, age, attempt count
 *      — never customer payload content) to the backend so ForgedOps
 *      ops can see field data at risk without inspecting content.
 */
export default function SyncSuccessListener() {
  const busyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      const snap = await getCombinedSnapshot();
      if (cancelled) return;
      noteSnapshot(snap);
      checkStuckPending(snap.pending);
      if (
        typeof navigator !== "undefined" &&
        navigator.onLine &&
        snap.pending > 0 &&
        !busyRef.current &&
        snap.state !== "syncing"
      ) {
        busyRef.current = true;
        const result = await drainAll();
        busyRef.current = false;
        if (result && result.succeeded > 0) {
          // Re-check immediately so noteSnapshot sees the post-drain
          // state on this same tick (drives the success toast).
          const after = await getCombinedSnapshot();
          if (!cancelled) noteSnapshot(after);
        }
      }
    };

    const onSyncSuccess = () => {
      toast.success("Offline work synced", {
        description: "Everything you did while disconnected has been saved.",
        duration: 4000,
      });
    };

    refresh();
    const offAny = onAnyChange(refresh);
    window.addEventListener("online", refresh);
    window.addEventListener("offline", refresh);
    window.addEventListener("forgedops:sync-success", onSyncSuccess);
    const iv = setInterval(refresh, 15000); // stuck-pending / drain safety poll

    return () => {
      cancelled = true;
      offAny();
      window.removeEventListener("online", refresh);
      window.removeEventListener("offline", refresh);
      window.removeEventListener("forgedops:sync-success", onSyncSuccess);
      clearInterval(iv);
    };
  }, []);

  return null;
}
